import {
  ipControllerManager,
  getNetworkInfo,
  type NetworkInfo,
  type IPControllerInfo
} from '#~/ipjoystick/index.js';
import { useEffectOnce } from '@studio/react-utils/hooks';
import { useState, useEffect, useRef, useCallback } from 'react';

export const useAddDevice = () => {
  const [netInfo, setNetInfo] = useState<NetworkInfo>();
  const [currentPort, setCurrentPort] = useState<number | undefined>();

  // ENG-4332: on the browser surface getNetworkInfo() resolves undefined (the gate
  // records guidance instead of throwing), so both setters already take the
  // undefined path and the panel renders its empty state rather than crashing the
  // effect with an uncaught rejection.
  const updateNetInfo = async () => {
    const info = await getNetworkInfo();
    setNetInfo(info);
    setCurrentPort(info?.port);
  };

  useEffectOnce(() => {
    updateNetInfo();
  });

  const addDevice = async ({ ip, port }: { ip: string; port: number }) => {
    await ipControllerManager.launchViscaBridge(ip, port);
  };

  return {
    netInfo,
    currentPort,
    addDevice,
    updateNetInfo
  };
};

export const useRemoveDevice = () => {
  const removeDevice = async (port: number) => {
    return ipControllerManager.stopProcess(port);
  };

  return removeDevice;
};

export const useRenameDevice = () => {
  const renameDevice = async (controller: IPControllerInfo, name: string) => {
    // Call the same launch function with the new name.
    await ipControllerManager.launchViscaBridge(controller.localIP, controller.port, name);
  };
  return renameDevice;
};

export const useRelaunchDevice = () => {
  // Same launch path as add/rename; Electron replaces the crashed entry with a fresh bridge.
  const relaunchDevice = async (controller: IPControllerInfo) => {
    await ipControllerManager.launchViscaBridge(
      controller.localIP,
      controller.port,
      controller.name,
      controller.controlledDeviceID
    );
  };
  return relaunchDevice;
};

export const useProcesses = () => {
  const [processes, setProcesses] = useState<IPControllerInfo[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const processes = await ipControllerManager.getProcessesArray();
      setProcesses(processes);
    } catch (err) {
      console.error(' >> Error fetching processes:', err);
      setProcesses([]); // Clear processes on error
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const reload = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return { processes, reload, isLoading };
};

export const useJoystickConnection = (ipPort: number | undefined) => {
  const [isConnected, setConnected] = useState(false);
  const ipPortRef = useRef(ipPort);

  useEffect(() => {
    ipPortRef.current = ipPort;
  }, [ipPort]);

  useEffectOnce(() => {
    const handleJoystickConnected = ({ port }: { port: number }) => {
      if (port === ipPortRef.current) {
        setConnected(true);
      }
    };
    ipControllerManager.onJoystickConnected(handleJoystickConnected);
  });
  return { isConnected, setConnected };
};
